import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { type CarConfigHighlightSection, CarConfigConfigureSection } from "../../utils/types/SectionTypes";
import type { CarConfigConfigureSectionsLbx } from "../../utils/types/SectionTypesLbx";
import { CarConfigStep } from "../../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import type { MinimizedBasketType } from "../../../../shared-logic/types/BasketType";

export type SectionType = CarConfigConfigureSection | CarConfigConfigureSectionsLbx | CarConfigHighlightSection;
export type PassedSections = SectionType[];

export type CarConfigNavigationType = {
    activeSection: CarConfigConfigureSection | CarConfigConfigureSectionsLbx | CarConfigHighlightSection;
    winterWheelTabActive: boolean;
    passedSection: PassedSections;
    defaultCarId: string;
    modelId: string;
    carId?: string;
    step: CarConfigStep;
    basketParams: MinimizedBasketType;
};

export const initialState: CarConfigNavigationType = {
    step: CarConfigStep.Configure,
    modelId: "",
    carId: "",
    basketParams: {},
    activeSection: CarConfigConfigureSection.Engine,
    winterWheelTabActive: false,
    passedSection: [], // Initialize passedSection as an empty array, we will fill this and remove based on when a section is passed through the viewport on scroll
    defaultCarId: "",
};

export type CarConfigNavigationActionType = ReturnType<
    | typeof setActiveSection
    | typeof handlePassedSection
    | typeof setDefaultCarId
    | typeof toggleWinterWheelTab
    | typeof setNavigationStep
    | typeof setNavigationIds
    | typeof setBasketParams
>;

export const CarConfigNavigationSlice = createSlice({
    name: "carConfigNavigation",
    initialState,
    reducers: {
        setDefaultCarId: (state, action: PayloadAction<string>) => {
            return {
                ...state,
                defaultCarId: action.payload,
            };
        },
        setNavigationStep: (state, action: PayloadAction<CarConfigStep>) => {
            return {
                ...state,
                step: action.payload,
            };
        },
        setNavigationIds: (state, action: PayloadAction<{ modelId: string; carId?: string }>) => {
            return {
                ...state,
                modelId: action.payload.modelId,
                carId: action.payload.carId,
            };
        },
        setBasketParams: (state, action: PayloadAction<MinimizedBasketType>) => {
            return {
                ...state,
                basketParams: action.payload,
            };
        },
        setActiveSection: (state, action: PayloadAction<SectionType>) => {
            return {
                ...state,
                activeSection: action.payload,
            };
        },
        handlePassedSection: (
            state,
            action: PayloadAction<{ newSection: SectionType; sectionsWithIntersectionRatio: Record<string, number> }>,
        ) => {
            const { newSection, sectionsWithIntersectionRatio } = action.payload;
            let passedSection: SectionType[] = [newSection]; // Initial null

            if (state.passedSection) {
                // If the section already exists, we want to remove the sections on top of that.
                const sectionIndex = state.passedSection.findIndex((section) => section === newSection);

                if (sectionIndex !== -1 && state.passedSection.length >= sectionIndex + 1) {
                    passedSection = [...state.passedSection];

                    // Filter out any sections on top of the new one, if they are entirely offscreen, aka with interesectionRatio 0.
                    state.passedSection.slice(sectionIndex + 1).forEach((sectionToRemove) => {
                        if (sectionsWithIntersectionRatio[sectionToRemove] === 0) {
                            passedSection = passedSection.filter((section) => section !== sectionToRemove);
                        }
                    });
                } else {
                    // If it doesn't yet exist, we add it to the state.
                    passedSection = [...state.passedSection, newSection].filter(
                        (value, index, self) => self.indexOf(value) === index,
                    );
                }
            }

            return {
                ...state,
                passedSection,
            };
        },
        toggleWinterWheelTab: (state) => {
            return {
                ...state,
                winterWheelTabActive: !state.winterWheelTabActive,
            };
        },
    },
});

export const {
    setActiveSection,
    handlePassedSection,
    setDefaultCarId,
    toggleWinterWheelTab,
    setNavigationStep,
    setNavigationIds,
    setBasketParams,
} = CarConfigNavigationSlice.actions;
